import { useEffect, useMemo, useState } from "react";
import { CurrencyDollar, CheckCircle, ChartLineUp, Crown, ArrowsLeftRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Th, Td } from "./TrainerSchedule";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../lib/useCurrency";

const periodLabel = (yyyymm) => {
  if (!yyyymm) return "All time";
  const [y, m] = yyyymm.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
};
const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthOptions = (count = 12) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < count; i++) {
    const p = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push(p);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};

export default function TrainerEarnings() {
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const MONEY = (n) => `${symbol}${Number(n || 0).toFixed(2)}`;
  const [period, setPeriod] = useState("");          // "" = all time
  const [scope, setScope] = useState(null);          // null = self/auto; trainer_id = pick coach (owner only)
  const [teamSummary, setTeamSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const months = useMemo(() => monthOptions(12), []);

  // Resolve which trainer to show details for:
  // - Owner: defaults to the selected scope (or own id when null)
  // - Non-owner: always own id
  const focusedTrainerId = user?.is_owner ? (scope || user?.id) : user?.id;

  const loadSummary = async () => {
    if (!user?.is_owner) return;
    try {
      const url = period ? `/trainers/earnings/summary?period=${period}` : "/trainers/earnings/summary";
      const { data } = await api.get(url);
      setTeamSummary(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const loadDetail = async () => {
    if (!focusedTrainerId) return;
    try {
      const qs = period ? `?period=${period}` : "";
      const { data } = await api.get(`/trainers/${focusedTrainerId}/earnings${qs}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { loadSummary(); loadDetail(); /* eslint-disable-next-line */ }, [period, focusedTrainerId]);

  const markPaid = async (id) => {
    try {
      await api.post(`/trainers/earnings/${id}/mark-paid`);
      toast.success("Marked as paid out");
      loadSummary(); loadDetail();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const payAllForFocused = async () => {
    if (!detail?.trainer) return;
    if (!window.confirm(`Mark ALL pending earnings for ${detail.trainer.name}${period ? ` in ${periodLabel(period)}` : ""} as paid?`)) return;
    try {
      const qs = period ? `?period=${period}` : "";
      const { data } = await api.post(`/trainers/${focusedTrainerId}/earnings/mark-all-paid${qs}`);
      toast.success(`${data.marked_paid} earnings marked paid`);
      loadSummary(); loadDetail();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="trainer-earnings">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ {user?.is_owner ? "Team commissions" : "My earnings"} ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Earnings.</h1>
          <p className="text-zinc-500 mt-2">
            {user?.is_owner
              ? "Each trainer earns their commission % of every payment made by their own clients."
              : "You earn your commission % of every payment made by your own clients."}
          </p>
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="period-filter">
          <button onClick={() => setPeriod("")} className={`tracker px-3 py-2 border ${period === "" ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`} data-testid="period-all">All time</button>
          <button onClick={() => setPeriod(currentPeriod())} className={`tracker px-3 py-2 border ${period === currentPeriod() ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`} data-testid="period-this-month">This month</button>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-xs tracker">
            <option value="">— Pick month —</option>
            {months.map((m) => <option key={m} value={m}>{periodLabel(m)}</option>)}
          </select>
        </div>
      </div>

      {/* Owner-only: team summary */}
      {user?.is_owner && teamSummary && (
        <div className="card-flat p-6" data-testid="team-summary">
          <div className="flex items-center gap-2 mb-4">
            <ChartLineUp size={18} weight="duotone" className="text-brand" />
            <div className="font-display text-2xl">Team breakdown {period && <span className="text-zinc-500 text-sm">— {periodLabel(period)}</span>}</div>
          </div>
          <div className="grid sm:grid-cols-4 gap-4 mb-6">
            <StatCard label="Gross collected" value={MONEY(teamSummary.totals.gross)} />
            <StatCard label="Paid to trainers" value={MONEY(teamSummary.totals.trainer_amount)} accent="text-emerald-400" />
            <StatCard label="Studio share" value={MONEY(teamSummary.totals.studio_amount)} accent="text-brand-light" />
            <StatCard label="Pending payout" value={MONEY(teamSummary.totals.pending)} accent="text-orange-400" />
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#0F0F0F]">
              <tr>
                <Th>Trainer</Th>
                <Th>Commission %</Th>
                <Th>Payments</Th>
                <Th>Gross</Th>
                <Th>Trainer cut</Th>
                <Th>Studio cut</Th>
                <Th>Pending</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {teamSummary.breakdown.map((b) => (
                <tr key={b.trainer.id} className="border-t border-white/5" data-testid={`summary-row-${b.trainer.id}`}>
                  <Td>
                    <div className="flex items-center gap-2">
                      {b.trainer.is_owner && <Crown size={12} weight="fill" className="text-[#FFC700]" />}
                      <span className="font-semibold">{b.trainer.name}</span>
                    </div>
                  </Td>
                  <Td><span className="font-mono">{(b.trainer.commission_percent ?? (b.trainer.is_owner ? 100 : 70)).toFixed(0)}%</span></Td>
                  <Td><span className="font-mono">{b.count}</span></Td>
                  <Td><span className="font-mono money">{MONEY(b.gross)}</span></Td>
                  <Td><span className="font-mono text-emerald-400 money">{MONEY(b.trainer_amount)}</span></Td>
                  <Td><span className="font-mono text-brand-light money">{MONEY(b.studio_amount)}</span></Td>
                  <Td><span className="font-mono text-orange-400 money">{MONEY(b.pending)}</span></Td>
                  <Td className="text-right">
                    <button
                      onClick={() => setScope(b.trainer.id)}
                      className="btn-ghost !py-1 !px-3 !text-[10px]"
                      data-testid={`focus-${b.trainer.id}`}
                    >
                      <ArrowsLeftRight size={11} weight="bold" /> Detail
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Per-trainer detail */}
      {detail && (
        <div data-testid="trainer-detail">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
            <div>
              <div className="tracker text-zinc-500">Detail</div>
              <div className="font-display text-3xl flex items-center gap-2">
                {detail.trainer?.is_owner && <Crown size={20} weight="fill" className="text-[#FFC700]" />}
                {detail.trainer?.name}
                <span className="font-mono text-base text-zinc-500">· {(detail.trainer?.commission_percent ?? (detail.trainer?.is_owner ? 100 : 70)).toFixed(0)}%</span>
              </div>
            </div>
            {user?.is_owner && detail.totals.pending_amount > 0 && (
              <button onClick={payAllForFocused} className="btn-primary" data-testid="mark-all-paid">
                                <CheckCircle size={14} weight="bold" /> Mark all pending paid (<span className="money">{MONEY(detail.totals.pending_amount)}</span>)
              </button>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <StatCard label="Lifetime earned" value={MONEY(detail.lifetime.total)} icon={<CurrencyDollar size={18} weight="duotone" />} />
            <StatCard label="This month" value={MONEY(detail.lifetime.this_month)} accent="text-emerald-400" />
            <StatCard label="Pending payout" value={MONEY(detail.lifetime.pending)} accent="text-orange-400" />
          </div>

          <div className="card-flat overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#0F0F0F]">
                <tr>
                  <Th>Period</Th>
                  <Th>Client</Th>
                  <Th>Description</Th>
                  <Th>Gross</Th>
                  <Th>%</Th>
                  <Th>Trainer cut</Th>
                  <Th>Studio cut</Th>
                  <Th>Status</Th>
                  {user?.is_owner && <Th className="text-right">Actions</Th>}
                </tr>
              </thead>
              <tbody>
                {detail.rows.map((r) => (
                  <tr key={r.id} className="border-t border-white/5" data-testid={`earning-row-${r.id}`}>
                    <Td><span className="font-mono text-xs">{r.period}</span></Td>
                    <Td><span className="font-semibold">{r.client?.name || "—"}</span></Td>
                    <Td><span className="text-zinc-400">{r.description}</span></Td>
                    <Td><span className="font-mono money">{MONEY(r.gross_amount)}</span></Td>
                    <Td><span className="font-mono">{Number(r.commission_percent).toFixed(0)}%</span></Td>
                    <Td><span className="font-mono text-emerald-400 money">{MONEY(r.trainer_amount)}</span></Td>
                    <Td><span className="font-mono text-brand-light money">{MONEY(r.studio_amount)}</span></Td>
                    <Td>
                      <span className={`tracker ${r.status === "paid" ? "text-emerald-400" : "text-orange-400"}`}>{r.status}</span>
                    </Td>
                    {user?.is_owner && (
                      <Td className="text-right">
                        {r.status === "pending" && (
                          <button onClick={() => markPaid(r.id)} className="btn-ghost !py-1 !px-3 !text-[10px]" data-testid={`mark-paid-${r.id}`}>
                            Mark paid
                          </button>
                        )}
                      </Td>
                    )}
                  </tr>
                ))}
                {detail.rows.length === 0 && (
                  <tr><td colSpan={user?.is_owner ? 9 : 8} className="px-6 py-10 text-center text-zinc-600">No earnings{period ? ` for ${periodLabel(period)}` : " yet"}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent, icon }) {
  return (
    <div className="card-flat p-5">
      <div className="flex items-center gap-2 tracker text-zinc-500 mb-2">{icon}{label}</div>
      <div className={`font-display text-3xl money ${accent || ""}`}>{value}</div>
    </div>
  );
}
